import React, { useState, useRef } from "react";

import { IoClose } from "react-icons/io5";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { signUpWithOptions } from "../../Services/authService";
import Cookies from "js-cookie";

const PinInput = ({ inputRefs, onPinChange, onSubmit }) => {
  const [pin, setPin] = useState(["", "", "", "", "", ""]);

  const handleChange = (index, value) => {
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    const fullPin = newPin.join("");
    if (onPinChange) onPinChange(fullPin);

    // Move focus to next input
    if (value !== "" && index < 5) inputRefs.current[index + 1].current.focus();

    // Auto-submit when 6 digits are entered
    if (fullPin.length === 6 && onSubmit) {
      onSubmit(fullPin); // pass fullPin directly
    }
  };

  const handleBackspace = (index, event) => {
    if (event.key === "Backspace" && index > 0 && pin[index] === "") {
      inputRefs.current[index - 1].current.focus();
    }
  };

  return (
    <div className="flex justify-center mb-4">
      {pin.map((value, index) => (
        <React.Fragment key={index}>
          <input
            ref={inputRefs.current[index]}
            type="password"
            maxLength={1}
            value={value}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleBackspace(index, e)}
            className="border border-blue-500 md:w-10 md:h-10 w-9 h-10 text-center mx-1 rounded shadow-lg shadow-blue-600"
            autoFocus={index === 0}
          />
          {index === 2 && (
            <span className="mt-2 text-sky-500 ml-1">&ndash;</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const OtpModalSignUp = ({
  isModalOpen,
  toggleModal,
  referenceId,
  formData,
  onSuccess,
  lastFourDigits,
  webConfirm,
}) => {
  const inputRefs = useRef(
    Array.from({ length: 6 }).map(() => React.createRef()),
  );
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");

  const handleValidationErrors = (res) => {
    const errors = res?.errors || res?.data?.errors;

    if (!errors) {
      const msg = res?.status?.message || "Validation error";
      showErrorToast(msg);
      setError(msg);
      return;
    }

    // 🔥 Flatten all errors
    const allErrors = Object.values(errors).flat();

    // ✅ Show all toasts
    allErrors.forEach((msg) => showErrorToast(msg));

    // ✅ Set first error in UI
    setError(allErrors[0]);
  };

  // Accept OTP as parameter for auto-submit
  // const handleSubmit = async (currentOtp = otp) => {
  //   if (currentOtp.length !== 6) {
  //     setError('Please enter a 6-digit OTP.');
  //     return;
  //   }

  //   if (!referenceId) {
  //     setError('Reference ID missing. Retry signup.');
  //     return;
  //   }

  //   const payload = {
  //     email_address: formData.email,
  //     mobile_number: formData.mobile,
  //     password: formData.password,
  //     confirm_password: formData.confirmPassword,
  //     website_login: true,
  //     company_domain: formData.website || "",
  //     otp: currentOtp,
  //     reference_id: referenceId,
  //   };

  //   try {
  //     const result = await signUpWithOptions(payload);

  //     if (result?.status?.code === 200) {
  //       Cookies.set('authTokenUser', result?.data?.token, { expires: 1 });
  //       showSuccessToast(result?.message || "Signup successful");
  //       onSuccess?.();
  //     } else {
  //       const msg = result?.message || "OTP verification failed";
  //       showErrorToast(msg);
  //       setError(msg);
  //     }
  //   } catch (err) {
  //     const msg = err.response?.data?.message || "OTP verification failed";
  //     showErrorToast(msg);
  //     setError(msg);
  //   }
  // };

  const handleSubmit = async (currentOtp = otp) => {
    setError("");
    if (currentOtp.length !== 6) {
      setError("Please enter a 6-digit OTP.");
      return;
    }

    if (!referenceId) {
      setError("Reference ID missing. Retry signup.");
      return;
    }

    // 🔹 Set true/false based on radio selection
    const websiteLogin = webConfirm === "yes";
    const companyDomain = websiteLogin ? formData.website : "";

    const payload = {
      referred_by: formData?.referredBy,
      email_address: formData.email,
      mobile_number: formData.mobile,
      password: formData.password,
      confirm_password: formData.confirmPassword,
      website_login: websiteLogin,
      company_domain: companyDomain,
      otp: currentOtp,
      reference_id: referenceId,
    };

    try {
      const result = await signUpWithOptions(payload);

      if (result) {
        console.log("result", result);
      }

      if (result?.status?.code === 200) {
        Cookies.set("authTokenUser", result?.data?.token, { expires: 1 });
        showSuccessToast(result?.message || "Signup successful");
        onSuccess?.();
        return;
      }

      if (
        (result?.status?.code === 422 || result?.status === 422) &&
        result?.data?.errors?.referred_by
      ) {
        showErrorToast(
          result?.errors?.referred_by[0] ||
            result?.data?.errors?.referred_by?.[0],
        );
        setError(
          result?.data?.errors?.referred_by?.[0] ||
            result?.errors?.referred_by[0],
        );
        return;
      }

      if (result?.data?.status === 422 || result?.status === 422) {
        showErrorToast(
          result?.data?.errors?.otp?.[0] || result?.errors?.otp[0],
        );
        setError(result?.data?.errors?.otp?.[0] || result?.errors?.otp[0]);
        return;
      }

      if (result?.status?.code === 422 && result?.errors) {
        Object.values(result.errors)
          .flat()
          .forEach((msg) => showErrorToast(msg));
        setError("Validation error. Please check your details.");
        return;
      }

      const msg =
        result?.errors?.otp[0] ||
        result?.data.errors?.otp[0] ||
        result?.message ||
        result?.status?.message ||
        result?.data?.message ||
        result?.data?.status?.message ||
        "OTP verification failed";

      showErrorToast(msg);
      setError(msg);
    } catch (err) {
      console.error("❌ OTP Submit Error:", err);

      const errRes = err?.response?.data || {};

      const msg =
        errRes?.errors?.otp[0] ||
        errRes?.data.errors?.otp[0] ||
        errRes?.status?.message ||
        errRes?.message ||
        err?.message ||
        "OTP verification failed";

      showErrorToast(msg);
      setError(msg);
    }
  };

  return (
    isModalOpen && (
      <div
        className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
        style={{ zIndex: 999999 }}
      >
        <div className="bg-white rounded-lg shadow-lg w-11/12 md:w-1/3">
          <div className="bg-gradient-to-r from-indigo-900 to-blue-700 text-white relative py-3 rounded-t-2xl text-center shadow-md">
            <p className="text-white text-xl mb-1">
              OTP sent to your mobile ending with{" "}
              <span className="font-semibold">{lastFourDigits}</span>
            </p>
            <IoClose
              color="white"
              className="text-2xl absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
              onClick={toggleModal}
            />
          </div>
          <div className="p-3">
            <PinInput
              inputRefs={inputRefs}
              onPinChange={setOtp}
              onSubmit={handleSubmit}
            />
            {error && (
              <div className="text-red-500 mt-1 text-center">{error}</div>
            )}
          </div>
          <div className="p-2 flex justify-center gap-3">
            <button
              type="button"
              className="bg-blue-900 text-white font-semibold rounded-md px-4 hidden py-2 hover:bg-blue-700"
              onClick={() => handleSubmit()}
            >
              Submit
            </button>
            <button
              type="button"
              className=" text-white bg-red-600 font-semibold rounded-md px-3 py-1 hover:bg-gray-500"
              onClick={toggleModal}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  );
};

export default OtpModalSignUp;
